import { forwardRef, useImperativeHandle, type ButtonHTMLAttributes, type ForwardedRef, type HTMLAttributes, type IframeHTMLAttributes, type SVGAttributes } from 'react'

import { useCssVars, type CssVars } from '@/hooks/useCssVars'

type WithCssVars<TProps> = Omit<TProps, 'style'> & {
  cssVars?: CssVars
}

function useForwardedCssVarRef<TElement extends HTMLElement | SVGElement>(
  cssVars: CssVars | undefined,
  forwardedRef: ForwardedRef<TElement>,
) {
  const innerRef = useCssVars<TElement>(cssVars)
  useImperativeHandle(forwardedRef, () => innerRef.current as TElement)
  return innerRef
}

export const CssVarDiv = forwardRef<HTMLDivElement, WithCssVars<HTMLAttributes<HTMLDivElement>>>(
  function CssVarDiv({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <div ref={ref} {...props} />
  },
)

export const CssVarSpan = forwardRef<HTMLSpanElement, WithCssVars<HTMLAttributes<HTMLSpanElement>>>(
  function CssVarSpan({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <span ref={ref} {...props} />
  },
)

export const CssVarButton = forwardRef<HTMLButtonElement, WithCssVars<ButtonHTMLAttributes<HTMLButtonElement>>>(
  function CssVarButton({ cssVars, type = 'button', ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <button ref={ref} type={type} {...props} />
  },
)

export const CssVarNav = forwardRef<HTMLElement, WithCssVars<HTMLAttributes<HTMLElement>>>(
  function CssVarNav({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <nav ref={ref} {...props} />
  },
)

export const CssVarIframe = forwardRef<HTMLIFrameElement, WithCssVars<IframeHTMLAttributes<HTMLIFrameElement>>>(
  function CssVarIframe({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <iframe ref={ref} {...props} />
  },
)

export const CssVarSvg = forwardRef<SVGSVGElement, WithCssVars<SVGAttributes<SVGSVGElement>>>(
  function CssVarSvg({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <svg ref={ref} {...props} />
  },
)

export const CssVarPath = forwardRef<SVGPathElement, WithCssVars<SVGAttributes<SVGPathElement>>>(
  function CssVarPath({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <path ref={ref} {...props} />
  },
)

export const CssVarLine = forwardRef<SVGLineElement, WithCssVars<SVGAttributes<SVGLineElement>>>(
  function CssVarLine({ cssVars, ...props }, forwardedRef) {
    const ref = useForwardedCssVarRef(cssVars, forwardedRef)
    return <line ref={ref} {...props} />
  },
)
